import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '../utils';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string; // applies to trigger button
  popoverClassName?: string; // popover content
  widthClassName?: string; // override width
  /** Absolute starting year override (takes precedence over span props) */
  startYear?: number;
  /** Absolute ending year override (takes precedence over span props) */
  endYear?: number;
  /** How many years back from current year to include if startYear not provided (default 25) */
  pastYearSpan?: number;
  /** How many years forward from current year to include if endYear not provided (default 25) */
  futureYearSpan?: number;
}

export function DatePicker({
  value,
  onChange,
  disabled,
  placeholder = 'Pick a date',
  className,
  popoverClassName,
  widthClassName = 'w-[240px]',
  startYear,
  endYear,
  pastYearSpan = 25,
  futureYearSpan = 25,
}: DatePickerProps) {
  const [internal, setInternal] = React.useState<Date | undefined>(value);

  // Keep internal in sync if parent drives value
  React.useEffect(() => {
    setInternal(value);
  }, [value]);

  const effective = value ?? internal;

  function handleSelect(d?: Date) {
    if (onChange) {
      onChange(d);
    } else {
      setInternal(d);
    }
  }

  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          data-empty={!effective}
          disabled={disabled}
          className={cn(
            'justify-start text-left font-normal',
            widthClassName,
            'data-[empty=true]:text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className='mr-2 h-4 w-4' />
          {effective ? format(effective, 'PPP') : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-auto p-0 pointer-events-auto', popoverClassName)}
        onOpenAutoFocus={e => {
          e.preventDefault();
        }}
      >
        <Calendar
          mode='single'
          selected={effective}
          captionLayout='dropdown'
          startMonth={(() => {
            const currentYear = new Date().getFullYear();
            let computedStart = startYear ?? currentYear - pastYearSpan;
            const selectedYear = effective?.getFullYear();
            if (selectedYear != null && selectedYear < computedStart) {
              computedStart = selectedYear; // ensure selected year visible
            }

            return new Date(computedStart, 0);
          })()}
          endMonth={(() => {
            const currentYear = new Date().getFullYear();
            let computedEnd = endYear ?? currentYear + futureYearSpan;
            const selectedYear = effective?.getFullYear();
            if (selectedYear != null && selectedYear > computedEnd) {
              computedEnd = selectedYear; // ensure selected year visible
            }

            return new Date(computedEnd, 11);
          })()}
          onSelect={d => {
            handleSelect(d ?? undefined);
          }}
          showOutsideDays
        />
      </PopoverContent>
    </Popover>
  );
}
